import React, { useState } from 'react';

import {
  LomavisLazyImage,
  LomavisLazyImageProps
} from '@/lomavisSharedUi/atoms/images/LomavisLazyImage/LomavisLazyImage';

export interface LomavisLazyImageWithBackgroundProps
  extends Omit<LomavisLazyImageProps, 'containerClassName'> {
  containerClassName?: string;
  backgroundClassName?: string;
}

/**
 * Atom: a lazy-loaded, uncropped image with a blurred background fill.
 * The parent owns the dimensions through the outer container.
 */
export const LomavisLazyImageWithBackground: React.FC<
  LomavisLazyImageWithBackgroundProps
> = ({
  src,
  className = '',
  containerClassName = '',
  backgroundClassName = '',
  onLoad,
  ...imageProps
}) => {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);

  return (
    <div
      className={`position-relative w-100 h-100 overflow-hidden ${containerClassName}`}
    >
      {src && loadedSrc === src ? (
        <div
          aria-hidden="true"
          data-testid="lazy-image-background"
          className={`position-absolute top-0 start-0 w-100 h-100 ${backgroundClassName}`}
          style={{
            backgroundImage: `url(${JSON.stringify(src)})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            filter: 'blur(20px)',
            transform: 'scale(1.1)'
          }}
        />
      ) : null}

      {/*
        Bootstrap comes from the embedding Django page, which still ships
        5.2 — the object-fit utilities only exist from 5.3 on. Without the
        inline objectFit the image stays on `fill`, stretches over the whole
        box and hides the background layer, so the style carries the
        behaviour and the class is only kept for 5.3+ hosts.
      */}
      <div className="position-relative w-100 h-100">
        <LomavisLazyImage
          {...imageProps}
          src={src}
          className={`object-fit-contain ${className}`}
          style={{ objectFit: 'contain', ...imageProps.style }}
          onLoad={(event) => {
            setLoadedSrc(src ?? null);
            onLoad?.(event);
          }}
        />
      </div>
    </div>
  );
};
